<template>
  <div class="source-content tagboards">
    <template v-if="!selectedTagboard">
      <div class="filter-bar">
        <search-filter
          :value="search"
          :options="sortedBoardTags"
          :selected="tags"
          data-test="producer-assets-searchBoards"
          @input="changeSocialBoardSearch"
          @update:selected="changeSocialBoardTags"
        />
      </div>
  
      <!-- No boards after searching -->
      <div v-if="!isLoading && !total && tags.length" class="text-center">
        <h3 class="margin-bottom-md">
          No tagboards found
        </h3>
  
        <button
          class="tb-btn tb-btn-contained medium-em"
          data-entity="producer-assets-resetTagboardSearch"
          data-test="producer-assets-resetBoardSearch"
          @click="clearTags"
        >
          Reset Search
        </button>
      </div>
  
      <div
        ref="sourceList"
        class="source-list"
        data-test="producer-assets-boards-list"
      >
        <draggable
          class="tagboard-list"
          v-model="tagboards"
          :clone="clone"
          :sort="false"
          :animation="200"
          :group="{ name: 'tagboard', pull: 'clone', put: false }"
          @start="onStart"
          :move="checkMove"
          revert-on-spill="true"
          @unchoose="onUnchoose"
        >
          <button
            v-for="tagboard in tagboards"
            :key="tagboard._id"
            class="source-item tagboard"
            data-entity="producer-assets-tagboard"
            :data-test="`producer-assets-socialBoard:${tagboard._id}`"
            @click="selectedTagboard = tagboard._id"
          >
            <div class="source-item-wrapper">
              <div
                v-if="hasAvatar(tagboard)"
                class="avatar margin-right-xs"
                :style="{ 'background-image' : 'url(' + getAvatarUrl(tagboard) + ')' }"
                :data-test="`producer-assets-socialBoard-avatar:${tagboard._id}`"
              />
  
              <div class="source-item-info-wrapper">
                <div class="source-title title-text" :title="tagboard.title" :data-test="`producer-assets-socialBoard-title:${tagboard._id}`">
                  {{ tagboard.name || tagboard.title }} 
                  <span
                    v-if="tagboard.description"
                    class="text-os-low"
                    :data-test="`producer-assets-socialBoard-description:${tagboard._id}`"
                  >| {{ tagboard.description }}</span>
                </div>

                <div
                :class="{
                  'source-item-tags': true,
                  'margin-top-xxs': tagboard.tags?.length,
                }"
                :data-test="`producer-assets-socialBoard-tags:${tagboard._id}`"
              >
                  <span
                    v-for="tag in tagboard.tags"
                    :key="tag"
                    class="tag"
                    :data-test="`producer-assets-socialBoard-tag:${tagboard._id}:${tag.toLowerCase()}`"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </button>
        </draggable>
  
        <div ref="loadTrigger" class="load-trigger">
          <div v-if="isLoading" class="loading loading--inline" data-test="producer-assets-boards-loading">
            <i class="icon icon-spinner"></i>
          </div>
        </div>
      </div>
    </template>

    <posts-list
      v-else
      :tagboard-id="selectedTagboard"
      @back="selectedTagboard = null"
    />
  </div>
</template>

<script>
import * as uuid from 'uuid';
import { mapState, mapGetters, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import tabMixins from '../../mixins/tab-mixins';

import toast from '../../components/toast/toast';
import PostsList from './PostsList.vue';

export default {
  mixins: [tabMixins],
  components: {
    PostsList,
    SearchFilter,
  },

  props: {
    tagboardId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      error: false,
    };
  },

  created() {
    this.getTagboardTags();
    this.$store.dispatch('getTagboards').catch((err) => {
      console.error('Error loading boards:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load boards.',
      });
    });
  },

  beforeDestroy() {
    this.$store.dispatch('setTagboards', []);
    this.intersectionObserver.disconnect();
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    tagboards() {
      if (!this.$refs.sourceList || !this.tagboards.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  methods: {
    ...mapActions([
      'getTagboardTags',
      'changeSocialBoardSearch',
      'changeSocialBoardTags',
    ]),

    addTag(tag) {
      this.$store.dispatch('addBoardsTag', tag);
    },

    removeTag(tag) {
      this.$store.dispatch('removeBoardsTag', tag);
    },

    clearTags() {
      this.$store.dispatch('resetSocialBoardSearch');
    },

    /**
    * Attempt to load more assets. Will not trigger a load if we are already loading,
    * or if there's nothing more to load
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreBoards) {
        this.$store.dispatch('incrementBoardsPage');
      }
    },

    clone(original) {
      const clone = pick(original, ['_id']);
      clone.timeline_id = uuid.v1();
      clone.type = 'tagboard';
      return clone;
    },

    onStart() {
      const dropzones = document.getElementsByClassName('tgbTagboardDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    checkMove(e) {
      // prevent multiple tagboards
      const hasContent = e.relatedContext.list.length > 0;

      if (hasContent) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = ('not-allowed');
        this.error = 'A full tagboard cannot be presented with other content.';
      } else {
        // got to set this back to false to prevent false error if user
        // passes over a forbidden zone on the way to an allowed zone
        this.error = false;
      }

      return !hasContent; // if in list pass false to cancel drop
    },

    onUnchoose(e) {
      e.item.style.cursor = 'pointer';

      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },

    getAvatarUrl(tagboard) {
      if (tagboard.avatar_url) {
        return tagboard.avatar_url;
      }

      return 'https://static.tagboard.com/producer/assets/img/logo-hash-t-white.svg';
    },

    hasAvatar(tagboard) {
      return tagboard.avatar_url;
    },
  },

  computed: {
    ...mapState({
      tagboards: (state) => state.tagboards.tagboards,
      isLoading: (state) => state.tagboards.isLoading,
      search: (state) => state.tagboards.search,
      tags: (state) => state.tagboards.tags,
      total: (state) => state.tagboards.total,
    }),

    ...mapGetters(['sortedBoardTags']),

    selectedTagboard: {
      get() {
        return this.tagboardId;
      },
      set(value) {
        this.$emit('update:tagboardId', value);
      },
    },
  },
};
</script>
